import {
  getAssetUrlInWorker,
  openConfirmationPanel,
  openGenericConfirmationPanel,
} from '@wix/wix-data-client-common'
import {
  DynamicPageError,
  DPErrorPanelData,
  DBDriverDynamicPageError,
} from '../../business-logic/dynamic-pages/dynamicPageErrors'
import { DPErrorCodes } from '../../business-logic/dynamic-pages/constants'
import type {
  I18n,
  EditorSDK,
  Logger,
} from '@wix/wix-data-client-common-standalone'

type Params = {
  logger: Logger
  editorSdkProxy: EditorSDK
  i18n: I18n
}

export const isDpError = (value: unknown): value is { error: unknown } =>
  typeof value === 'object' && value !== null && 'error' in value

const findErrorCodeFromError = (error: unknown): string | undefined =>
  Object.values(DPErrorCodes).find(code => String(error).includes(code))

const findErrorCode = (error: any, klass: any): string | null =>
  !error
    ? null
    : error instanceof klass
      ? (error as any).code
      : findErrorCode(error.cause, klass)

export const createDPErrorHandler =
  <T = unknown>({ logger, editorSdkProxy, i18n }: Params) =>
  async (fn: () => Promise<T>) => {
    try {
      return await fn()
    } catch (error: unknown) {
      logger.error(
        new Error('Dynamic page error handler caught error', { cause: error }),
      )

      const t = i18n.t.bind(i18n)

      const errorCode =
        findErrorCode(error, DynamicPageError) ||
        findErrorCode(error, DBDriverDynamicPageError) ||
        findErrorCodeFromError(error)

      if (errorCode) {
        const { title, text, buttonLabel, helpId } = DPErrorPanelData[
          errorCode
        ]({
          t,
        })

        openConfirmationPanel({
          editorSdkProxy,
          logger,
          headerText: title,
          descriptionText: text,
          mainActionText: buttonLabel,
          helpId,
          illustration: await getAssetUrlInWorker(
            editorSdkProxy,
            'panel-icons/generic_warning.svg',
          ),
        })
      } else {
        openGenericConfirmationPanel({
          editorSdkProxy,
          logger,
          t,
        })
      }

      return { error }
    }
  }
